import React, { FC, useEffect, useRef, useMemo, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Slider, { Settings } from "react-slick";

import { IActiveLearningLesson } from "../../../../state";

import { DEFAULT_PAGE, SLIDER_DURATION_MS } from "./constants";
import Controls from "./Controls";
import styles from "./ProjectionSlider.module.scss";

type Props = {
  lesson: IActiveLearningLesson | null;
};

const SETTINGS: Settings = {
  accessibility: false,
  arrows: false,
  dots: false,
  infinite: false,
  speed: SLIDER_DURATION_MS,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: false,
  vertical: true,
  waitForAnimate: false,
};

const ProjectionSlider: FC<Props> = ({ lesson }) => {
  const history = useHistory();
  const location = useLocation();
  const slider = useRef<Slider>(null);

  const params = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);
  const pageParam = Number(params.get("page")) || DEFAULT_PAGE;
  const projectionImageUrls = lesson?.projection_image_urls || [];
  const maxPage = projectionImageUrls.length;
  const currentPage = (() => {
    if (maxPage === 0) return DEFAULT_PAGE;
    if (pageParam > maxPage) return DEFAULT_PAGE;
    if (pageParam < DEFAULT_PAGE) return DEFAULT_PAGE;

    return pageParam;
  })();

  const createPageSearch = useCallback(
    (page: number) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("page", String(page));

      return `?${nextParams.toString()}`;
    },
    [params],
  );

  const changePage = useCallback(
    (page: number) => {
      history.push({
        search: createPageSearch(page),
      });
    },
    [history, createPageSearch],
  );

  useEffect(() => {
    if (maxPage === 0) return;
    if (pageParam === currentPage) return;

    history.replace({
      search: createPageSearch(currentPage),
    });
  }, [currentPage, history, maxPage, pageParam, createPageSearch]);

  useEffect(() => {
    if (maxPage === 0) return;
    slider.current?.slickGoTo(currentPage - 1, false);
  }, [currentPage, maxPage]);

  return (
    <div className={styles.projectionSlider}>
      <Slider
        className={styles.slider}
        ref={slider}
        initialSlide={currentPage - 1}
        {...SETTINGS}
      >
        {projectionImageUrls.map((url, index) => (
          <div className={styles.container} key={index}>
            <img
              className={styles.image}
              src={`https://admin.gear.enageed.net${url}`}
              alt={`guideline-${index}`}
            />
          </div>
        ))}
      </Slider>
      <Controls
        currentPage={currentPage}
        maxPage={maxPage}
        onChangePage={changePage}
      />
    </div>
  );
};

export default ProjectionSlider;
